import { Badge } from "@exhousing/design-system";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { DocumentsPanel } from "@/components/DocumentsPanel";
import { CorrespondencePanel } from "@/components/inbox/CorrespondencePanel";
import { NotesPanel } from "@/components/NotesPanel";
import { LandlordDetailsCard } from "@/components/party/LandlordDetailsCard";
import { PortalAccessButton } from "@/components/party/PortalAccessButton";
import { OwnerPayoutPanel } from "@/components/party/OwnerPayoutPanel";
import { useLandlord } from "@/hooks/api/useParties";

export function LandlordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: landlord, isLoading, isError } = useLandlord(id);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !landlord)
    return <p className="text-sm text-destructive">Landlord not found.</p>;

  const c = landlord.contact;
  return (
    <div className="space-y-6">
      <Link
        to="/parties"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Landlords &amp; tenants
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            {c.display_name}
          </h1>
          <div className="mt-2 flex gap-2">
            <Badge tone="brand">{landlord.landlord_type}</Badge>
            {landlord.category && <Badge tone="neutral">Tier {landlord.category}</Badge>}
          </div>
        </div>
        <PortalAccessButton
          landlordId={landlord.id}
          defaultEmail={c.email}
          defaultFirstName={c.first_name}
          defaultLastName={c.last_name}
        />
      </header>

      <LandlordDetailsCard landlord={landlord} />

      <OwnerPayoutPanel landlordId={landlord.id} />

      <DocumentsPanel
        subjectType="landlord"
        subjectId={landlord.id}
        hint="Brokerage agreements, ID, ownership proof, correspondence."
      />
      <CorrespondencePanel entityType="contact" entityId={c.id} />
      <NotesPanel subjectType="landlord" subjectId={landlord.id} />
    </div>
  );
}
